/**
 * The DOMContentLoaded event listener to run the `scrollToTopButton` function
 * once the whole DOM has fully loaded.
 * @event document#DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', () => {
  scrollToTopButton();
  initializeYouTubeVideos();
  initializeVimeoVideos();
  initializeWistiaVideos();
  initializeIframeVideos();
  searchButton();
  initializeFooterMenuToggle();
  initMegaNav();
  initSubmenuTabs();
  initPopups();
  mobileMenuHandler();
  initMobileSlideSubmenu();
  // showAnnouncementBar();
});

/**
 * Close popup by Escape key.
 */
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    closePopup();
  }
});

/**
 * Manages search boxes activation/deactivation. The search button ('.js-search-btn') or the
 * close button ('.js-search-close') toggle the 'active' state of the search box ('.js-search-box').
 * Supports multiple search boxes, toggling 'active' state for each individually.
 */
function searchButton() {
  const searchBox = document.querySelectorAll('.js-search-box');
  const header = document.querySelector('.js-header');

  const addToggleListener = (element, item) => {
    element?.addEventListener('click', e => {
      e.preventDefault();
      item.classList.toggle('active-search');
    });
  };

  searchBox?.forEach(searchItem => {
    const searchBtn = searchItem.querySelector('.js-search-btn');
    const searchBtnMobile = searchItem.querySelector('.js-search-btn-mobile');
    const closeBtn = searchItem.querySelector('.js-search-close');
    const cleanBtn = searchItem.querySelector('.js-search-clean');

    addToggleListener(searchBtn, header);
    addToggleListener(closeBtn, header);
    if (searchBtnMobile) {
      searchBtnMobile?.addEventListener('click', e => {
        e.preventDefault();
        e.target.closest('form').submit();
      });
    }
    cleanBtn?.addEventListener('click', e => {
      const form = e.target.closest('form');
      form.querySelector('input[name="s"]').value = '';
      form.submit();
    });
  });
}

/**
 * Selector function for the "Go to top" button.
 * @returns {HTMLElement} The "Go to top" button element.
 * */
function getTopButton() {
  return document.querySelector('.js-top-button');
}

/**
 * Scroll to the top of the page smoothly when the "Go to top" button is clicked.
 * */
function scrollToTopButton() {
  const topButton = getTopButton();
  if (topButton) {
    topButton.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }
}

/**
 * Asynchronously loads scripts provided in the `arr`. The scripts are loaded in order.
 * If script loading encounters an error, it skips to the next script without blocking the rest.
 *
 * @param {string[]} arr - An array of script URLs to be loaded.
 */
function loadscripts(arr) {
  (function scriptLoader(e, t, r) {
    let a;
    const i = e.scripts[0];
    const o = 'onreadystatechange';
    const f = 'readyState';
    const d = [];

    function shiftLoad() {
      let c;
      for (; d[0] && d[0][f] === 'loaded';) {
        c = d.shift();
        c[o] = !i.parentNode.insertBefore(c, i);
      }
    }

    while (r.length > 0) {
      const s = r.shift();
      a = e.createElement(t);
      if ('async' in i) {
        a.async = false;
        e.head.appendChild(a);
      } else if (i[f]) {
        d.push(a);
        a[o] = shiftLoad;
      } else {
        e.write(`<${t} src="${s}" defer></${t}>`);
      }
      a.src = s;
    }
  })(document, 'script', arr);
}

/**
 * Initializes YouTube video-playing functionality.
 * This function identifies YouTube video elements on the page and sets up the video thumbnail
 * and play button. Click events are added that replace the thumbnail with the actual
 * embedded video player upon clicking.
 */
function initializeYouTubeVideos() {
  const youTubeVideos = document.querySelectorAll('.js-youtube');

  const setVideoAttributes = (videoNode, thumbnailUrl) => {
    const imgElement = document.createElement('img');
    imgElement.style.cursor = 'pointer';
    imgElement.width = 1120;
    imgElement.height = 630;
    imgElement.src = thumbnailUrl;

    const playButton = document.createElement('div');
    playButton.className = 'play';

    videoNode.appendChild(imgElement);
    videoNode.appendChild(playButton);
  };

  const videoClickHandler = videoNode => {
    const iframeHTML = `
       <iframe
          width="1120"
          height="630"
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          src="https://www.youtube.com/embed/${videoNode.dataset.embed}?enablejsapi=1&rel=0&showinfo=0&autoplay=1"
          allowFullScreen="allowfullscreen"
       ></iframe>`;

    videoNode.innerHTML += iframeHTML;

    const imgElement = videoNode.querySelector('img');
    const playButton = videoNode.querySelector('.play');

    if (imgElement) imgElement.style.display = 'none';
    if (playButton) playButton.style.display = 'none';
  };

  youTubeVideos.forEach(videoNode => {
    const thumbnailUrl = videoNode.dataset.thumbSrc;
    setVideoAttributes(videoNode, thumbnailUrl);
  });

  document.addEventListener('click', e => {
    if (e.target.matches('img, .play')) {
      const youtubeEl = e.target.closest('.js-youtube');
      if (youtubeEl) {
        videoClickHandler(youtubeEl);
      }
    }
  });
}

/**
 * Initializes Vimeo video-playing functionality.
 * This function identifies Vimeo video elements on the page and sets up the video thumbnail
 * and play button. Click events are added that replace the thumbnail with the actual
 * embedded video player upon clicking.
 */
function initializeVimeoVideos() {
  const vimeoVideos = document.querySelectorAll('.js-vimeo');

  const setVideoAttributes = (videoNode, thumbnailUrl) => {
    const imgElement = document.createElement('img');
    imgElement.style.cursor = 'pointer';
    imgElement.width = 1120;
    imgElement.height = 630;
    imgElement.src = thumbnailUrl;

    const playButton = document.createElement('div');
    playButton.className = 'play';

    videoNode.appendChild(imgElement);
    videoNode.appendChild(playButton);
  };

  const videoClickHandler = videoNode => {
    const iframeHTML = `
       <iframe
          width="1120"
          height="630"
          frameBorder="0"
          allowfullscreen="allowfullscreen"
          src="https://player.vimeo.com/video/${videoNode.dataset.embed}?autoplay=1"
       ></iframe>`;

    videoNode.innerHTML += iframeHTML;

    const imgElement = videoNode.querySelector('img');
    const playButton = videoNode.querySelector('.play');

    if (imgElement) imgElement.style.display = 'none';
    if (playButton) playButton.style.display = 'none';
  };

  vimeoVideos.forEach(videoNode => {
    const thumbnailUrl = videoNode.dataset.thumbSrc;
    setVideoAttributes(videoNode, thumbnailUrl);
  });

  document.addEventListener('click', e => {
    if (e.target.matches('img, .play')) {
      const vimeoEl = e.target.closest('.js-vimeo');
      if (vimeoEl) {
        videoClickHandler(vimeoEl);
      }
    }
  });
}

/**
 * This function sets up the interaction for Wistia-hosted videos on the page.
 * For each Wistia video present on the page, it fetches the video's thumbnail
 * image, binds click events to the placeholder image, and pushes the video settings
 * into the _wq global settings array for the Wistia player.
 */
function initializeWistiaVideos() {
  const wistiaElements = document.querySelectorAll('.js-wistia-video-box');

  for (let i = 0; i < wistiaElements.length; i++) {
    const thumbnailSource = wistiaElements[i].dataset.thumbSrc;
    const oEmbedUrl = `https://fast.wistia.net/oembed?url=https://home.wistia.com/medias/${wistiaElements[i].dataset.embed}?embedType=async&videoWidth=640`;

    fetchThumbnailData(oEmbedUrl, i, wistiaElements, thumbnailSource);

    const VIDEO_ID = wistiaElements[i].dataset.embed;

    // eslint-disable-next-line no-underscore-dangle
    if (typeof window._wq === 'undefined') {
      // eslint-disable-next-line no-underscore-dangle
      window._wq = [];
    }

    // eslint-disable-next-line no-underscore-dangle
    window._wq.push({ id: VIDEO_ID, options: { videoFoam: true, autoPlay: true } });
  }

  const wistiaClickHandler = videoNode => {
    loadscripts([
      `//fast.wistia.com/embed/medias/${videoNode.dataset.embed}.jsonp`,
      '//fast.wistia.com/assets/external/E-v1.js',
    ]);

    const placeholderElement = videoNode.querySelector('.js-wistia-placeholder');
    if (placeholderElement) placeholderElement.remove();

    videoNode.querySelector('.js-wistia-video').classList.add('showed');
  };

  document.addEventListener('click', e => {
    if (e.target.matches('img, .play')) {
      const wistiaEl = e.target.closest('.js-wistia-video-box');
      if (wistiaEl) {
        wistiaClickHandler(wistiaEl);
      }
    }
  });
}

/**
 * Fetches video thumbnail data from a provided URL or uses an already provided source image.
 * If a source image is provided, the function will skip the fetch request.
 * It will call addWistiaPlaceholderImage with the provided source.
 *
 * addWistiaPlaceholderImage is called when:
 * - The fetch request is done and successful.
 * - A photo source is already provided.
 *
 * @param {string} url - Endpoint from which to fetch the thumbnail data.
 * @param {number} index - Index of current Wistia element in `wistiaElements`.
 * @param {NodeListOf<HTMLElement>} wistiaElements - NodeList (or array) of Wistia video elements.
 * @param {string} [source] - Source URL of the image. If provided, fetching is skipped.
 */
function fetchThumbnailData(url, index, wistiaElements, source) {
  if (source) {
    addWistiaPlaceholderImage(source, index, wistiaElements);
    return;
  }
  fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
    .then(response => response.json())
    .then(response => {
      source = response.thumbnail_url;
      addWistiaPlaceholderImage(source, index, wistiaElements);
    });
}

/**
 * Adds a placeholder image to Wistia video elements.
 *
 * @param {string} source - The URL of the placeholder image.
 * @param {number} index - The index of the current Wistia element in the `wistiaElements` array.
 * @param {HTMLElement[]} wistiaElements - An array of Wistia video elements.
 */
function addWistiaPlaceholderImage(source, index, wistiaElements) {
  const image = new Image();
  image.src = source;
  function handleImageLoad() {
    wistiaElements[index].querySelector('.js-wistia-placeholder').innerHTML = '<div class="play"></div>';
    wistiaElements[index].querySelector('.js-wistia-placeholder').appendChild(image);
  }

  image.addEventListener('load', handleImageLoad);
}

/**
 * Initializes video playing functionality for iframe videos.
 * Adds click handlers to play buttons and overlays to start video
 * playback and hide buttons and overlays.
 *
 * When the play button or overlay of a video is clicked,
 * we trigger the video playback by modifying the src of the iframe with autoplay=1.
 * Then we hide the overlay and play button for a seamless viewing experience.
 */
function initializeIframeVideos() {
  const iframeVideoClickHandler = videoNode => {
    // Start the video
    const iframe = videoNode.querySelector('iframe');
    const { src } = iframe;
    iframe.src = src + (src.includes('?') ? '&autoplay=1' : '?autoplay=1');

    // Hide overlay use JavaScript hide() function
    const overlay = videoNode.querySelector('.js-video-overlay');
    const playBtn = videoNode.querySelector('.play');
    overlay.style.display = 'none';
    playBtn.style.display = 'none';
    videoNode.classList.add('showed');
  };

  document.addEventListener('click', e => {
    if (e.target.matches('img, .play')) {
      const iframeVideoEl = e.target.closest('.js-iframe-video');
      if (iframeVideoEl) {
        iframeVideoClickHandler(iframeVideoEl);
      }
    }
  });
}

/**
 * Manages the mobile menu. Components ('.js-mob-menu-btn', '.js-close-mob-menu',
 * '.js-mob-menu-shadow', and '.js-has-mob-submenu') have event listeners that toggle
 * the 'active' and 'mob-menu-active' classes on interaction.
 * The mobile menu button opens the menu. The close button and 'shadow' close it.
 * Clicking a submenu item makes it active and prevents the default event.
 */
function mobileMenu() {
  const body = document.querySelector('body');
  const mobMenu = document.querySelector('.js-mob-menu');
  const mobOpenBtn = document.querySelector('.js-mob-menu-btn');
  const mobCloseBtns = document.querySelectorAll('.js-close-mob-menu');
  const mobShadow = document.querySelector('.js-mob-menu-shadow');
  const mobSubmenus = document.querySelectorAll('.js-has-mob-submenu');

  const toggleMobileMenu = state => {
    if (state === 'open') {
      mobMenu?.classList.add('active');
      body?.classList.add('mob-menu-active');
    } else {
      mobMenu?.classList.remove('active');
      body?.classList.remove('mob-menu-active');
    }
  };

  const handleSubmenuClicks = submenu => {
    const btn = submenu.querySelector('.js-list-link') || submenu.querySelector('.menu-link');
    const submenuContent = submenu.querySelector('.js-mob-submenu');
    const backBtn = submenu.querySelector('.js-back-btn');

    backBtn.addEventListener('click', () => submenuContent.classList.remove('active'));
    btn?.addEventListener('click', e => {
      e.preventDefault();
      submenuContent.classList.add('active');
    });
  };

  mobOpenBtn?.addEventListener('click', () => toggleMobileMenu('open'));
  mobShadow?.addEventListener('click', () => toggleMobileMenu('close'));

  mobCloseBtns?.forEach(btn => btn.addEventListener('click', () => toggleMobileMenu('close')));
  mobSubmenus?.forEach(handleSubmenuClicks);
}

/**
 * This function controls the behavior of the announcement bar: shows it for new users or
 *  after clearing cache, and allows the user to close it. The display
 *  status is stored in localStorage.
 */
function showAnnouncementBar() {
  const bannerClosedTime = localStorage.getItem('bannerClosedTime');

  if (!bannerClosedTime || isMoreThan10Days(new Date(parseInt(bannerClosedTime)))) {
    document.querySelector('.announcement-bar').style.display = 'block';
  }

  document
    .querySelector('.announcement-bar__close-button')
    .addEventListener('click', closeAnnouncementBar);
}

function isMoreThan10Days(closedTime) {
  const TEN_DAYS = 10 * 24 * 60 * 60 * 1000; // 10 days in milliseconds
  const currentTime = new Date().getTime();

  return currentTime - closedTime > TEN_DAYS;
}

function closeAnnouncementBar() {
  document.querySelector('.announcement-bar').style.display = 'none';
  localStorage.setItem('bannerClosedTime', new Date().getTime().toString());
}

// Function to initialize the toggle behavior
function initializeFooterMenuToggle() {
  const menuItems = document.querySelectorAll('.nav-footer .subtitle-col');

  // Add a click event listener to each .nav-item element
  menuItems.forEach(menuItem => {
    menuItem.addEventListener('click', function (event) {
      event.preventDefault(); // Prevent default link behavior

      // Check if the screen width is less or equal to 768px
      if (window.innerWidth <= 768) {
        menuItems.forEach(item => {
          if (item !== this) {
            item.parentElement.classList.remove('active');
          }
        });

        // Toggle the 'active' class
        this.parentElement.classList.toggle('active');
      }
    });
  });
}

function initMegaNav() {
  const megaNavItems = document.querySelectorAll('.js-has-submenu');

  megaNavItems.forEach(item => {
    const link = item.querySelector('.js-lint-link');

    link?.addEventListener('click', () => {
      // Close other active items
      megaNavItems.forEach(otherItem => {
        if (otherItem !== item) {
          otherItem.classList.remove('active');
        }
      });

      // Toggle current item
      item.classList.toggle('active');
    });
  });

  document.addEventListener('click', e => {
    // Check if click is outside submenu__content and js-lint-link
    if (!e.target.closest('.submenu__box') && !e.target.closest('.js-lint-link')) {
      megaNavItems.forEach(item => {
        item.classList.remove('active');
      });
    }
  });
}

function initSubmenuTabs() {
  const submenuTypeDItems = document.querySelectorAll('.submenu-type-d');

  submenuTypeDItems.forEach(submenu => {
    const tabButtons = submenu.querySelectorAll('.js-tabs-nav-item');
    const tabContents = submenu.querySelectorAll('.js-tabs-content');

    tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        // Remove active from all tabs
        tabButtons.forEach(btn => btn.classList.remove('active'));
        tabContents.forEach(content => content.classList.remove('active'));

        // Add active to current tab
        button.classList.add('active');
        if (tabContents[index]) {
          tabContents[index].classList.add('active');
        }
      });
    });

    tabButtons[0].classList.add('active');
    tabContents[0].classList.add('active');
  });
}

function initPopups() {
  const popupButtons = document.querySelectorAll('.js-popup');

  if (popupButtons.length) {
    const wrapper = document.createElement('div');
    wrapper.className = 'popup-section__wrapper';

    const popup = document.createElement('div');
    popup.className = 'popup-section__popup';

    const contentContainer = document.createElement('div');
    contentContainer.className = 'popup-section__content';

    const closeButton = document.createElement('span');
    closeButton.className = 'popup-section__close';

    popup.appendChild(closeButton);
    popup.appendChild(contentContainer);
    wrapper.appendChild(popup);
    document.body.appendChild(wrapper);

    popupButtons.forEach(popupButton => {
      popupButton.addEventListener('click', e => {
        e.preventDefault();
        // Get the value of the data-popup attribute
        const popupId = popupButton.dataset.popup;

        // Find the matching .popup__content element
        const popupContent = document.querySelector(
          `.popup-section__hidden_content[data-popup="${popupId}"]`,
        );

        // Add the 'active' class if the element is found
        if (popupContent) {
          const popupType = popupContent.dataset.popup_type;
          contentContainer.innerHTML = popupContent.innerHTML;

          wrapper.className = 'popup-section__wrapper';
          wrapper.classList.add('popup-active');
          wrapper.classList.add(popupType);

          // Add 'popup-open' class to body to prevent scrolling
          document.body.classList.add('popup-open');

          // Attach scripts.
          popupContent.querySelectorAll('script')?.forEach(script => {
            const newScript = document.createElement('script');

            if (script.src) {
              newScript.src = script.src;
              newScript.async = false;
            } else {
              newScript.textContent = script.textContent;
            }

            document.body.appendChild(newScript);
            document.body.removeChild(newScript);
          });

          zoomImage(wrapper);

          // Fix for Safari.
          setTimeout(() => {
            window.dispatchEvent(new UIEvent('resize'));
          }, 100);
        }
      });
    });

    closeButton.addEventListener('click', e => {
      e.preventDefault();
      // document.body.classList.remove('hidden');
      closePopup();
    });

    // eslint-disable-next-line func-names
    wrapper.addEventListener('click', e => {
      if (e.target === wrapper) {
        closePopup();
      }
    });
  }
}

/**
 * Close popup.
 */
function closePopup() {
  const popups = document.querySelectorAll('.popup-section__wrapper.popup-active');
  popups.forEach(popup => {
    popup.classList.remove('popup-active');
    document.body.classList.remove('popup-open');
    popup.querySelector('.popup-section__content').innerHTML = '';
  });
}

// eslint-disable-next-line consistent-return
function zoomImage(modalContent) {
  const zoomInBtn = modalContent.querySelector('.zoom-in');
  const zoomOutBtn = modalContent.querySelector('.zoom-out');

  if (!zoomOutBtn || !zoomInBtn) {
    return false;
  }

  const modalImg = modalContent.querySelector('.popup-section__media img');
  modalImg.setAttribute('draggable', 'false');
  let zoomLevel = 1;
  const maxZoom = 2;
  const minZoom = 1;
  let startX = 0;
  let startY = 0;
  let isDragging = false;
  let offsetX = 0;
  let offsetY = 0;
  let coordX = 0;
  let coordY = 0;
  const defaultWidth = modalImg.offsetWidth;
  const defaultHeight = modalImg.offsetHeight;

  zoomInBtn.addEventListener('click', () => {
    if (zoomLevel < maxZoom) {
      modalContent.classList.add('zoomIn');
      zoomLevel = maxZoom;
      updateImageTransform();
      zoomInBtn.style.display = 'none';
      zoomOutBtn.style.display = 'block';
    }
  });

  zoomOutBtn.addEventListener('click', () => {
    if (zoomLevel > minZoom) {
      modalContent.classList.remove('zoomIn');
      resetZoom();
      zoomInBtn.style.display = 'block';
      zoomOutBtn.style.display = 'none';
    }
  });

  function resetZoom() {
    zoomLevel = minZoom;
    startX = 0;
    startY = 0;
    updateImageTransform(true);
  }

  function updateImageTransform(reset = false) {
    if (reset) {
      modalImg.style.transform = 'translate3d(0, 0, 0)';
      modalImg.style.width = 'auto';
      modalImg.style.height = 'auto';
      return;
    }

    const imgWidth = defaultWidth * zoomLevel;
    const imgHeight = defaultHeight * zoomLevel;

    if (zoomLevel === maxZoom) {
      const bounds = getBounds(imgWidth, imgHeight);
      startX = bounds.leftX !== 0 ? bounds.leftX / 2 : 0;
      startY = bounds.topY !== 0 ? bounds.topY / 2 : 0;
    }

    modalImg.style.transform = `translate3d(${startX}px, ${startY}px, 0)`;
    modalImg.style.width = `${imgWidth}px`;
    modalImg.style.height = `${imgHeight}px`;
  }

  function getBounds(imgWidth, imgHeight) {
    const windowWidth = modalContent.clientWidth;
    const windowHeight = modalContent.clientHeight;
    const leftX = windowWidth - imgWidth || 0;
    const topY = windowHeight - imgHeight || 0;

    return {
      leftX,
      topY,
    };
  }

  function startDrag(e) {
    if (zoomLevel > 1) {
      const clientX = e.clientX || e.touches[0].clientX;
      const clientY = e.clientY || e.touches[0].clientY;

      isDragging = true;
      offsetX = clientX;
      offsetY = clientY;
      coordX = startX;
      coordY = startY;
      modalImg.classList.add('grabbing');
    }
  }

  function moveDrag(e) {
    if (isDragging) {
      const clientX = e.clientX || e.touches[0].clientX;
      const clientY = e.clientY || e.touches[0].clientY;

      startX = coordX + clientX - offsetX;
      startY = coordY + clientY - offsetY;

      modalImg.style.transform = `translate3d(${startX}px, ${startY}px, 0)`;
    }
  }

  function endDrag() {
    isDragging = false;
    modalImg.classList.remove('grabbing');
  }

  modalImg.addEventListener('mousedown', startDrag);
  window.addEventListener('mousemove', moveDrag);
  window.addEventListener('mouseup', endDrag);

  modalImg.addEventListener('touchstart', startDrag, { passive: true });
  window.addEventListener('touchmove', moveDrag, { passive: true });
  window.addEventListener('touchend', endDrag);
}

// Mobile Menu Handler
const mobileMenuHandler = () => {
  const mobileMenuBtn = document.querySelector('.js-mobile-menu-btn');
  const mobileMenu = document.querySelector('.js-mobile-menu');
  const closeBtn = document.querySelector('.js-menu-close');
  const submenuLinks = document.querySelectorAll('.js-mobile-lint-link');
  const { body } = document;

  if (!mobileMenuBtn || !mobileMenu) return;

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    mobileMenu.classList.toggle('active');
    body.classList.toggle('menu-open');
  };

  // Handle submenu toggles
  const handleSubmenu = e => {
    const parent = e.target.closest('.js-has-submenu');
    if (!parent) return;

    const submenu = parent.querySelector('.js-mobile-submenu');
    if (!submenu) return;

    // Close other open submenus and remove active class from their links
    const otherSubmenus = document.querySelectorAll('.js-mobile-submenu.active');
    otherSubmenus.forEach(menu => {
      if (menu !== submenu) {
        menu.classList.remove('active');
        const parentItem = menu.closest('.js-has-submenu');
        parentItem.classList.remove('active');
        const link = parentItem.querySelector('.js-mobile-lint-link');
        if (link) link.classList.remove('active');
      }
    });

    // Toggle current submenu and link
    parent.classList.toggle('active');
    submenu.classList.toggle('active');
    e.target.classList.toggle('active');
  };

  // Event Listeners
  mobileMenuBtn.addEventListener('click', toggleMobileMenu);
  closeBtn?.addEventListener('click', toggleMobileMenu);

  submenuLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      handleSubmenu(e);
    });
  });

  // Close menu on escape key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && mobileMenu.classList.contains('active')) {
      toggleMobileMenu();
    }
  });

  // Close menu when clicking outside
  document.addEventListener('click', e => {
    if (
      mobileMenu.classList.contains('active')
      && !e.target.closest('.js-mobile-menu')
      && !e.target.closest('.js-mobile-menu-btn')
    ) {
      toggleMobileMenu();
    }
  });

  const searchInput = document.querySelector('#searchform-mob input');
  const clearButton = document.querySelector('.clear-search');

  if (searchInput && clearButton) {
    // Show/hide clear button based on input value
    searchInput.addEventListener('input', () => {
      clearButton.classList.toggle('active', searchInput.value.length > 0);
    });

    // Clear input when clear button is clicked
    clearButton.addEventListener('click', () => {
      searchInput.value = '';
      clearButton.classList.remove('active');
      searchInput.focus();
    });
  }
};

// Mobile Slide Submenu Handler
const initMobileSlideSubmenu = () => {
  const slideSubmenus = document.querySelectorAll('.js-has-slide-submenu');

  slideSubmenus.forEach(submenu => {
    const button = submenu.querySelector('.js-mobile-lint-link');
    const tabsContainer = submenu.querySelector('.js-mobile-submenu-tabs');
    const contentContainer = submenu.querySelector('.js-mobile-submenu-content');
    const tabs = submenu.querySelectorAll('.js-mobile-submenu-tab');
    const panels = submenu.querySelectorAll('.js-mobile-submenu-panel');
    const backButtons = submenu.querySelectorAll('.js-mobile-submenu-back');
    const submenuLevel1 = submenu.querySelector('.js-mobile-slidemenu');

    button.addEventListener('click', () => {
      button.classList.toggle('active');
      submenuLevel1.classList.toggle('active');
    });

    // Show content screen
    const showContent = tabIndex => {
      tabsContainer.style.transform = 'translateX(-100%)';
      contentContainer.style.transform = 'translateX(0)';

      // Update active states
      tabs.forEach(tab => tab.classList.remove('active'));
      panels.forEach(panel => panel.classList.remove('active'));

      tabs[tabIndex].classList.add('active');
      panels[tabIndex].classList.add('active');
    };

    // Show tabs screen
    const showTabs = () => {
      tabsContainer.style.transform = 'translateX(0)';
      contentContainer.style.transform = 'translateX(100%)';
    };

    // Tab click handler
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        showContent(index);
      });
    });

    // Back button click handler
    backButtons.forEach(backBtn => {
      backBtn.addEventListener('click', e => {
        e.preventDefault();
        if (contentContainer.style.transform === 'translateX(0px)') {
          showTabs();
        } else {
          button.classList.remove('active');
          submenuLevel1.classList.remove('active');
        }
      });
    });
  });
};
